<template>
    <v-form ref="form" v-model="valid" data-cy="kpi-form" class="pb-4">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="6" class="align-center">
                <div class="width-max-content ml-auto">
                    <v-switch
                        v-model="formData.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="formData.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </div>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="4">
                <TextFieldLocale
                    v-model="formData.name[activeLangCode]"
                    :label="`${$t('title')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="title"
                    :rules="[requiredRule('title')]"
                />
                <div class="text-subtitle-1 font-weight-medium pb-1">{{ $t('period') }}*</div>
                <order-service-daterange
                    :start-date-label="`${$t('date_from')}*`"
                    :end-date-label="`${$t('date_to')}*`"
                    dense
                    data-cy="date-range"
                    :single-line="false"
                    :clearable="true"
                    hide-details="auto"
                    :_start-date="formData.startDate"
                    :_end-date="formData.endDate"
                    :rules="[requiredRule('date'), dateRangeWithinOneYear()]"
                    @change-start-date="formData.startDate = $event"
                    @change-end-date="formData.endDate = $event"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="6">
                <v-radio-group v-model="formData.type" density="compact" hide-details class="mb-6">
                    <v-radio
                        v-for="{value, title} in useDateType"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="4">
                <div class="text-subtitle-1 font-weight-bold">{{ $t('set_kpi') }}</div>
                <div v-if="isShowValidationError" class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular">
                    {{ `${$t('kpi_validation_error')}` }}
                </div>
                <div class="text-subtitle-1 font-weight-medium">{{ $t('turnovers') }}</div>
                <v-row dense>
                    <v-col class="flex-grow-0">
                        <v-select
                            v-model="formData.paymentKpis[0].currencyCode"
                            :items="useCurrency"
                            :label="`${$t('currency')}*`"
                            :rules="[requiredRule('currency')]"
                            density="compact"
                            data-cy="turnovers-currency"
                            min-width="160"
                        />
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="formData.paymentKpis[0].price"
                            type="number"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            hide-spin-buttons
                            data-cy="turnovers-price"
                        />
                    </v-col>
                </v-row>
                <div class="text-subtitle-1 font-weight-medium">{{ $t('income') }}</div>
                <v-row dense>
                    <v-col class="flex-grow-0">
                        <v-select
                            v-model="formData.paymentKpis[1].currencyCode"
                            :items="useCurrency"
                            :label="`${$t('currency')}*`"
                            :rules="[requiredRule('currency')]"
                            density="compact"
                            data-cy="income-currency"
                            min-width="160"
                        />
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="formData.paymentKpis[1].price"
                            type="number"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            hide-spin-buttons
                            data-cy="income-price"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="6">
                <v-checkbox
                    v-model="formData.historyCompare"
                    hide-details
                    :label="$t('compare_with_historical_data')"
                    density="compact"
                    class="pb-4"
                    data-cy="history-compare"
                />
                <TextareaLocale
                    v-model="formData.internalNote[activeLangCode]"
                    :label="$t('internal_note')"
                    data-cy="internal-note"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import {merge, omit} from 'lodash-es'

    const props = defineProps<{
        kpiId?: number | null
        kpi?: object | null
    }>()

    const emit = defineEmits<{
        (e: 'cancel'): void
        (e: 'save', data): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()
    const router = useRouter()

    const valid = ref<boolean>(false)
    const isShowValidationError = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    provide('activeLangCode', activeLangCode)
    const isLoading = ref<boolean>(false)
    const systemCurrency = authStore.systemCurrency || persistentStore.currency
    const formData = ref({
        active: true,
        name: {},
        internalNote: {},
        historyCompare: false,
        paymentKpis: [
            {currencyCode: systemCurrency, price: null, priceType: 'TURNOVERS'},
            {currencyCode: systemCurrency, price: null, priceType: 'INCOME'},
        ],
        startDate: null,
        endDate: null,
        type: 'BOOKING',
    })

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (props.kpiId) {
            formData.value = merge(formData.value, omit(props.kpi, 'paymentKpis'))
            formData.value.paymentKpis = formData.value.paymentKpis.map(paymentItem => {
                const matchingItem = props.kpi.paymentKpis?.find(item => item.priceType === paymentItem.priceType)
                return matchingItem ? {...paymentItem, ...matchingItem} : paymentItem
            })
            formData.value.startDate = new Date(formData.value.startDate)
            formData.value.endDate = new Date(formData.value.endDate)
        }
    })

    const save = async (): Promise<void> => {
        isShowValidationError.value = false
        const data = clone(formData.value)
        data.paymentKpis = data.paymentKpis.filter(({price}) => price)

        if (data.paymentKpis?.length > 0) {
            try {
                isLoading.value = true
                if (props.kpiId) {
                    const resp = await app.$api.kpi.put(props.kpiId, data)
                    emit('save', resp)
                } else {
                    const res = await app.$api.kpi.post(data)
                    await router.push({name: 'kpi', params: {id: res.id}})
                }
                app.$toast.success(t('success_message.saved_successfully'))
            } catch (e) {
                app.$toast.error(e.msg || e.errors[0].message)
            } finally {
                isLoading.value = false
            }
        } else {
            isShowValidationError.value = true
        }
    }

    const dateRangeWithinOneYear = () => {
        const start = new Date(formData.value.startDate)
        const end = new Date(formData.value.endDate)
        const oneYearLater = new Date(start)
        oneYearLater.setFullYear(start.getFullYear() + 1)
        return end <= oneYearLater || t('validation.one_year_date_range')
    }

    const cancel = (): void => {
        if (props.kpiId) router.push({name: 'kpis'})
        if (props.kpiId === 0) emit('cancel')
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const useDateType = computed(() => {
        return [
            {value: 'BOOKING', title: t('kpiDateType.BOOKING')},
            {value: 'START', title: t('kpiDateType.START')},
            {value: 'END', title: t('kpiDateType.END')},
        ]
    })
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(.v-selection-control-group) {
        flex-direction: row;
    }
    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        :deep(.v-selection-control-group) {
            flex-direction: column;
        }
    }
</style>
